import { useQuery } from '@tanstack/react-query'

import { useConsole } from '@/app/useConsole'

import {
  fetchAuthPaths, fetchSession, loginUrl, logoutUrl,
  type AuthPaths, type SessionState,
} from './session'

/**
 * The caller's own session, as React state.
 *
 * Refetched on window focus like everything else — an operator who leaves a tab
 * open over lunch should discover an expired session by being asked to log in,
 * not by a mutation failing halfway through.
 *
 * It never retries. A 401 is an answer, not a failure, and retrying it three
 * times just delays the login prompt.
 */
export function useSession(): SessionState {
  const { api } = useConsole()
  const { data } = useQuery({
    queryKey: ['session'],
    queryFn: () => fetchSession(api),
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })
  return data ?? { status: 'loading' }
}

/**
 * Which identity plane this deployment signs staff into (#215).
 *
 * Fetched EAGERLY rather than at the click, and that is the point: a signed-out
 * operator is the one who needs the sign-in link, and making them wait on a
 * round trip after pressing a button is how a working button still feels dead.
 * `signIn` awaits it only if it has not arrived yet.
 *
 * `retry: false` and no focus refetch. A backend with no such route is the
 * ordinary case on an older deployment, and asking it three times does not make
 * it appear; `fetchAuthPaths` resolves that to `null` rather than throwing, so
 * there is nothing here for an error boundary to catch.
 */
export function useAuthPaths(): AuthPaths | null {
  const { config } = useConsole()
  const { data } = useQuery({
    queryKey: ['auth-paths'],
    queryFn: () => fetchAuthPaths(config),
    retry: false,
    // Which plane a deployment uses changes when it is redeployed, not while an
    // operator is looking at it.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  })
  return data ?? null
}

/**
 * Send the operator to Ory to sign in, or out.
 *
 * A full-page navigation, deliberately: the login flow sets an httpOnly cookie
 * on a third-party origin and comes back, which an XHR cannot do. It also means
 * the console holds no token and never sees a credential.
 *
 * # Why these are async now (#215)
 *
 * The path depends on which identity plane this backend serves staff on, and a
 * signed-out operator has no `/me` answer to read it from. If the eager query
 * has not resolved when the button is pressed, the navigation WAITS for it
 * rather than proceeding on the fallback — because proceeding is what produced
 * the reported bug: a sign-in that navigates to the customer plane, mints a
 * customer cookie, and returns to a console still showing "Sign in".
 *
 * A race that resolves to the wrong plane most of the time and the right one
 * occasionally is worse than either, because it cannot be reproduced on demand.
 */
export function useAuthNavigation() {
  const { config } = useConsole()
  // The session, when there is one, publishes the paths this backend serves the
  // identity provider's self-service surface under. It is read here rather than
  // in `session.ts` so the path an operator is sent to comes from the backend
  // that answered rather than from a constant this repo maintains a copy of.
  const state = useSession()
  const session = state.status === 'signed-in' ? state.session : null
  const discovered = useAuthPaths()

  // A signed-in operator already carries the authoritative pair, so there is
  // nothing to wait for. Only the signed-out case can need the round trip, and
  // it is the only case that was broken.
  const paths = async (): Promise<AuthPaths | null> =>
    session !== null ? null : discovered ?? await fetchAuthPaths(config)

  return {
    signIn: async (returnTo: string = window.location.href) => {
      window.location.assign(loginUrl(config, returnTo, session, await paths()))
    },
    signOut: async (returnTo: string = window.location.origin) => {
      window.location.assign(logoutUrl(config, returnTo, session, await paths()))
    },
  }
}
